import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text, LoadingSpinner } from '../ui'
import { supportCenterReportsService } from '../../services/supportCenterReports'
import type { SupportCenterFormKind } from '../../types/supportCenterSpecialties'
import { SUPPORT_CENTER_FORM_KIND_LABELS } from '../../types/supportCenterSpecialties'
import type { SupportCenterHistoryItem } from '../../types/supportCenterReports'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import { TRIAGE_STATUS_LABELS } from '../../types/supportCenterTriage'
import { appointmentStatusBadgeClass } from './dashboardHelpers'
import { triageColumnBadgeClass } from './triagemHelpers'

type AppointmentPatientHistoryModalProps = {
  open: boolean
  patientCpf: string
  patientName?: string
  formKind: SupportCenterFormKind
  /** Data/hora da consulta de referência: lista só esta e as anteriores. */
  untilScheduledAt: string
  onClose: () => void
  onSelectRow?: (row: SupportCenterHistoryItem) => void
}

function formatCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

function formatDateTime(value: string): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR')
}

export const AppointmentPatientHistoryModal: React.FC<AppointmentPatientHistoryModalProps> = ({
  open,
  patientCpf,
  patientName,
  formKind,
  untilScheduledAt,
  onClose,
  onSelectRow,
}) => {
  const [rows, setRows] = React.useState<SupportCenterHistoryItem[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return

    let cancelled = false
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await supportCenterReportsService.listPatientHistoryByFormKind({
          patient_cpf: patientCpf,
          form_kind: formKind,
          until_scheduled_at: untilScheduledAt,
        })
        if (!cancelled) setRows(data)
      } catch (e) {
        if (!cancelled) {
          setRows([])
          setError(e instanceof Error ? e.message : 'Não foi possível carregar o histórico.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, patientCpf, formKind, untilScheduledAt])

  if (!open) return null

  const kindLabel = SUPPORT_CENTER_FORM_KIND_LABELS[formKind]
  const clickable = typeof onSelectRow === 'function'

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              Histórico - {kindLabel}
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              {patientName ? `${patientName} · ` : ''}
              CPF {formatCpf(patientCpf)}
            </Text>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {error && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm mb-3">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center">
              <Text size="sm" color="muted">
                Nenhum atendimento {kindLabel.toLowerCase()} encontrado para este paciente até esta
                consulta.
              </Text>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Data
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Especialidade
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Triagem
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Profissional
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.map((row) => (
                    <tr
                      key={row.appointment_id}
                      className={
                        clickable
                          ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/80'
                          : undefined
                      }
                      onClick={clickable ? () => onSelectRow(row) : undefined}
                    >
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                        {formatDateTime(row.scheduled_at)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                        {row.specialization_name || '—'}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${appointmentStatusBadgeClass(
                            row.status
                          )}`}
                        >
                          {APPOINTMENT_STATUS_LABELS[row.status]}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${triageColumnBadgeClass(
                            row.triage_status
                          )}`}
                        >
                          {row.triage_status ? TRIAGE_STATUS_LABELS[row.triage_status] : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                        {row.attendant_email || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
